import MoreInfoButton from "@/components/more-info-button";
import StyledTooltip from "@/components/styled-tooltip";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmailDeliveryChain, EmailReceivedHeadersChain, EmailReceivedHeader } from "@/types/postman.types";
import { ArrowRight, ChevronDown, Globe, Info } from "lucide-react";

const EmailHealthDeliveryChain = ({
  deliveryChain,
}: {
  deliveryChain: EmailDeliveryChain[] | EmailReceivedHeadersChain[];
}) => {
  console.log('deliveryChain:', deliveryChain);
  console.log('deliveryChain type:', typeof deliveryChain);
  console.log('deliveryChain length:', deliveryChain?.length);
  // Type guard to check if it's EmailDeliveryChain
  const isEmailDeliveryChain = (item: any): item is EmailDeliveryChain => {
    return item && typeof item.index === 'number' && typeof item.delay === 'string';
  };

  // Type guard to check if it's EmailReceivedHeader
  const isEmailReceivedHeader = (item: any): item is EmailReceivedHeader => {
    return item && item.by && item.by.value;
  };
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-lg font-semibold text-foreground">
          Email Delivery Chain
        </h2>
        <MoreInfoButton slug="Email-Delivery-Chain" />
      </div>
      <div className="grid gap-4">
        <Collapsible defaultOpen>
          <Card className="overflow-hidden">
            <CollapsibleTrigger className="w-full">
              <CardHeader className="p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Globe className="w-4 h-4 text-blue-500" />
                    <span>Receiving Chain</span>
                    <StyledTooltip description="The receiving chain shows the step-by-step routing and processing time of your email across SMTP servers.">
                      <Info className="w-3 h-3 text-muted-foreground" />
                    </StyledTooltip>
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200 [&[data-state=open]>svg]:rotate-180" />
                  </div>
                </div>
                <CardDescription className="self-start text-left">
                  Step-by-step routing and processing time of your email across
                  SMTP servers
                </CardDescription>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-x-auto">
              <CardContent className="p-0 overflow-x-auto border-t">
                <Table className="w-full overflow-x-auto">
                  <TableHeader>
                    <TableRow className="border-b bg-muted/50">
                      <TableHead className="py-2 px-4 text-left font-medium hidden lg:table-cell">
                        #
                      </TableHead>
                      <TableHead className="py-2 px-4 text-left font-medium hidden lg:table-cell">
                        Delay
                      </TableHead>
                      <TableHead className="py-2 px-4 text-left font-medium hidden lg:table-cell">
                        From
                      </TableHead>
                      <TableHead className="py-2 px-4 text-center w-8 hidden lg:table-cell"></TableHead>
                      <TableHead className="py-2 px-4 text-left font-medium hidden lg:table-cell">
                        To
                      </TableHead>
                      <TableHead className="py-2 px-4 text-left font-medium hidden lg:table-cell">
                        Protocol
                      </TableHead>
                      <TableHead className="py-2 px-4 text-left font-medium hidden lg:table-cell">
                        Time received
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="block sm:table-row-group w-full">
                    {deliveryChain?.map(
                      (hop: any, index: number) => {
                        // Handle EmailDeliveryChain type
                        if (isEmailDeliveryChain(hop)) {
                          return (
                            <TableRow
                              key={index}
                              className="block sm:table-row w-full"
                            >
                              <TableCell className="block sm:table-cell py-2 sm:py-2 px-4 text-sm w-full sm:w-auto">
                                <span className="hidden lg:block">{index}</span>
                                {/* Mobile */}
                                <div className="lg:hidden">
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      Step:
                                    </span>
                                    {index}
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      Delay:
                                    </span>
                                    <span className="text-green-600">
                                      {hop?.delay || ""}
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      From:
                                    </span>
                                    <span className="font-mono break-all">
                                      {hop?.from || ""}
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      To:
                                    </span>
                                    <span className="font-mono break-all">
                                      {hop?.to || ""}
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground  min-w-[70px]">
                                      Protocol:
                                    </span>
                                    <span className="font-mono break-all">
                                      {hop?.protocol || ""}
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      Time received:
                                    </span>
                                    <span className="font-mono break-all">
                                      {hop?.timeReceived || ""}
                                    </span>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-2 px-4 text-green-600 hidden lg:table-cell break-all">
                                {hop?.delay || ""}
                              </TableCell>
                              <TableCell className="py-2 px-4 font-mono hidden lg:table-cell break-all">
                                {hop?.from || ""}
                              </TableCell>
                              <TableCell className="py-2 px-4 text-center hidden lg:table-cell">
                                <ArrowRight className="w-4 h-4 text-muted-foreground inline" />
                              </TableCell>
                              <TableCell className="py-2 px-4 font-mono hidden lg:table-cell break-all">
                                <div className="flex items-center gap-2">
                                  {hop?.to || ""}
                                </div>
                              </TableCell>
                              <TableCell className="py-2 px-4 hidden lg:table-cell break-all">
                                {hop?.protocol || ""}
                              </TableCell>
                              <TableCell className="py-2 px-4 text-muted-foreground hidden lg:table-cell break-all">
                                {hop?.timeReceived || ""}
                              </TableCell>
                            </TableRow>
                          );
                        }
                        
                        // Handle EmailReceivedHeader type
                        if (isEmailReceivedHeader(hop)) {
                          return (
                            <TableRow
                              key={index}
                              className="block sm:table-row w-full"
                            >
                              <TableCell className="block sm:table-cell py-2 sm:py-2 px-4 text-sm w-full sm:w-auto">
                                <span className="hidden lg:block">{index}</span>
                                {/* Mobile */}
                                <div className="lg:hidden">
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      Step:
                                    </span>
                                    {index}
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      Delay:
                                    </span>
                                    <span className="text-green-600">
                                      {/* Calculate delay or show N/A for raw headers */}
                                      N/A
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      From:
                                    </span>
                                    <span className="font-mono break-all">
                                      {hop?.from?.value || ""}
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      To:
                                    </span>
                                    <span className="font-mono break-all">
                                      {hop?.by?.value || ""}
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground  min-w-[70px]">
                                      Protocol:
                                    </span>
                                    <span className="font-mono break-all">
                                      {hop?.with?.value || ""}
                                    </span>
                                  </div>
                                  <div className="flex gap-2">
                                    <span className="text-muted-foreground min-w-[70px]">
                                      Time received:
                                    </span>
                                    <span className="font-mono break-all">
                                      {hop?.timestamp || ""}
                                    </span>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-2 px-4 text-green-600 hidden lg:table-cell break-all">
                                {/* No delay info for raw headers */}
                                N/A
                              </TableCell>
                              <TableCell className="py-2 px-4 font-mono hidden lg:table-cell break-all">
                                {hop?.from?.value || ""}
                              </TableCell>
                              <TableCell className="py-2 px-4 text-center hidden lg:table-cell">
                                <ArrowRight className="w-4 h-4 text-muted-foreground inline" />
                              </TableCell>
                              <TableCell className="py-2 px-4 font-mono hidden lg:table-cell break-all">
                                <div className="flex items-center gap-2">
                                  {hop?.by?.value || ""}
                                </div>
                              </TableCell>
                              <TableCell className="py-2 px-4 hidden lg:table-cell break-all">
                                {hop?.with?.value || ""}
                              </TableCell>
                              <TableCell className="py-2 px-4 text-muted-foreground hidden lg:table-cell break-all">
                                {hop?.timestamp || ""}
                              </TableCell>
                            </TableRow>
                          );
                        }
                        
                        // Fallback for unknown type
                        return null;
                      }
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    </div>
  );
};

export default EmailHealthDeliveryChain;
